
import { useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useSystemSettings } from "@/hooks/useSystemSettings";
import { logError } from "@/lib/logger";

const emailSettingsSchema = z.object({
  senderEmail: z
    .string()
    .email("Please enter a valid email address")
    .min(1, "Sender email is required"),
});

type EmailSettingsFormValues = z.infer<typeof emailSettingsSchema>;

export function EmailSettings() {
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  // Track if settings have been loaded to prevent infinite loops
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const { getSetting, updateSetting, isLoading: settingsLoading } = useSystemSettings();

  // Initialize form with default values
  const form = useForm<EmailSettingsFormValues>({
    resolver: zodResolver(emailSettingsSchema),
    defaultValues: {
      senderEmail: "", // Start with empty until we load from DB
    },
  });

  // Load current settings from database when component mounts
  useEffect(() => {
    // Only fetch settings once
    if (!settingsLoaded) {
      const loadSettings = async () => {
        setIsInitialLoading(true);
        try {
          const senderEmail = await getSetting('sender_email', 'post@developr.no');
          console.log("Loaded sender email:", senderEmail);
          form.setValue('senderEmail', senderEmail);
          setSettingsLoaded(true);
        } catch (error) {
          console.error("Error fetching email settings:", error);
          logError("Failed to load email settings", "EmailSettings.tsx", { error });
          // Load default value on error
          form.setValue('senderEmail', "post@developr.no");
          toast.error("Failed to load email settings");
          setSettingsLoaded(true);
        } finally {
          setIsInitialLoading(false);
        }
      };

      loadSettings();
    }
  }, [form, getSetting, settingsLoaded]);

  async function onSubmit(data: EmailSettingsFormValues) {
    console.log("Submitting form with data:", data);
    setIsSaving(true);
    try {
      const success = await updateSetting('sender_email', data.senderEmail);
      if (success) {
        toast.success("Email settings saved successfully");
      } else {
        toast.error("Failed to save email settings");
      }
    } catch (error) {
      console.error("Error saving email settings:", error);
      logError("Failed to save email settings", "EmailSettings.tsx", { error });
      toast.error("An error occurred while saving settings");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Email Settings</CardTitle>
        <CardDescription>
          Configure email sender settings used throughout the system.
        </CardDescription>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="senderEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sender Email Address</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="email@example.com" 
                      {...field} 
                      disabled={false} // Make sure the input is never disabled
                    />
                  </FormControl>
                  <FormDescription>
                    This email address will be used as the sender for all outgoing emails.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
          <CardFooter>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? "Saving..." : "Save Settings"}
            </Button>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
}
